import React from 'react';
import { ArrowsLeftRight, DeviceMobile, SignIn, SignOut, UsersThree, WarningCircle } from '@phosphor-icons/react';
import { AnteilRing } from '../../../../components/immobilien/ui';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import type { EigKennzahlen, EigWechsel } from '../../../../lib/eigentuemer/liste';

/** Widgets der Eigentümerliste (Handoff Eigentümer §6.1, Screens 01/60). */
export function EigentuemerWidgets({ k, wechsel, onAktiv, onSaldo, onEigentuemer }: {
  k: EigKennzahlen;
  wechsel: readonly EigWechsel[];
  onAktiv: () => void;
  onSaldo: () => void;
  onEigentuemer: (id: string) => void;
}) {
  const tastatur = (aktion: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aktion(); }
  };
  const saldoBekannt = k.offenerSaldo !== null;
  return (
    <section aria-label="Übersicht Eigentümer" className="flex flex-col" style={{ gap: 14 }}>
      <div role="button" tabIndex={0} aria-label="Aktive Eigentümer anzeigen" onClick={onAktiv} onKeyDown={tastatur(onAktiv)}
        className="immo-akzent relative flex flex-col overflow-hidden cursor-pointer" style={{ minHeight: 150, borderRadius: 26, padding: 18 }}>
        <UsersThree weight="fill" size={150} aria-hidden className="absolute pointer-events-none" style={{ right: -14, bottom: -26, opacity: 0.12 }} />
        <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 700 }}><UsersThree weight="fill" size={15} />Aktive Eigentümer</span>
        <div className="flex items-end" style={{ marginTop: 'auto', gap: 16 }}>
          <span className="immo-zahl" style={{ fontSize: 58, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 0.95 }}>{k.aktiv}</span>
          <div className="flex flex-col" style={{ gap: 2, paddingBottom: 4 }}>
            <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700 }}
              title={k.ohneVerguetung > 0 ? `Bei ${k.ohneVerguetung} verwalteten Eigentümern ist noch keine Vergütung je Einheit hinterlegt` : undefined}>
              {k.verguetung === null ? '–' : eur(k.verguetung, { nachkomma: 0 })} Verwaltervergütung / Monat
            </span>
            <span style={{ fontSize: 12, color: 'var(--acc-ink2)' }}>{k.mv} MV · {k.weg} WEG · {k.sev} SEV · {k.uebernahme} Übernahme geplant</span>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gridAutoRows: 'minmax(150px, auto)', gap: 14 }}>
        <div
          role={saldoBekannt ? 'button' : undefined}
          tabIndex={saldoBekannt ? 0 : undefined}
          aria-label={saldoBekannt ? 'Eigentümer mit offenem Saldo anzeigen' : undefined}
          onClick={saldoBekannt ? onSaldo : undefined}
          onKeyDown={saldoBekannt ? tastatur(onSaldo) : undefined}
          className={`immo-glas flex flex-col ${saldoBekannt ? 'immo-hover-widget cursor-pointer' : ''}`}
          style={{ borderRadius: 26, padding: 16, gap: 6 }}
        >
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--bad)' }}><WarningCircle weight="fill" size={15} />Offener Saldo</span>
          <div className="flex flex-col" style={{ marginTop: 'auto', gap: 2 }}>
            <span className="immo-zahl" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: saldoBekannt ? 'var(--bad)' : 'var(--ink3)' }}>
              {k.offenerSaldo === null ? '–' : eur(k.offenerSaldo, { nachkomma: 0 })}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink2)' }}>{saldoBekannt ? `${k.saldoAnzahl} Eigentümer mit offenem Saldo` : BUCHHALTUNG_FOLGT}</span>
          </div>
        </div>

        <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 16, gap: 6 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' }}><DeviceMobile weight="fill" size={15} />Eigentümerportal</span>
          <div className="flex flex-1 items-center" style={{ gap: 12 }}>
            <AnteilRing prozent={k.portalQuote} />
            <div className="flex flex-col" style={{ gap: 2, fontSize: 12, color: 'var(--ink2)' }}>
              <span><b style={{ color: 'var(--ink)' }}>{k.portalAktiv}</b> aktiv</span>
              <span title="Einladungen werden noch angebunden"><b style={{ color: 'var(--ink)' }}>–</b> eingeladen</span>
            </div>
          </div>
        </div>
      </div>

      <div className="immo-glas flex flex-col md:flex-row md:items-center" style={{ borderRadius: 26, padding: '14px 16px', gap: 14 }}>
        <span className="flex items-center whitespace-nowrap" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' }}><ArrowsLeftRight weight="fill" size={15} />Wechsel &amp; Mandatsende</span>
        <div className="flex flex-1 flex-wrap min-w-0 w-full" style={{ gap: 10 }}>
          {wechsel.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Übernahmen oder Mandatsenden geplant</span>}
          {wechsel.map(w => {
            const rein = w.art === 'uebernahme';
            const farbe = rein ? 'var(--info)' : 'var(--warn)';
            const IconKomp = rein ? SignIn : SignOut;
            return (
              <button key={`${w.art}-${w.id}`} type="button" onClick={() => onEigentuemer(w.id)}
                className="immo-hover-flaeche flex items-center min-w-0 cursor-pointer text-left"
                style={{ flex: '1 1 220px', gap: 10, padding: '10px 12px', borderRadius: 16, border: 'none', background: 'var(--fill)', color: 'var(--ink)' }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: rein ? 'var(--info-soft)' : 'var(--warn-soft)' }}>
                  <IconKomp weight="fill" size={17} color={farbe} />
                </span>
                <span className="flex flex-col flex-1 min-w-0">
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{w.name}</span>
                  <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{rein ? 'Übernahme' : 'Abgabe / Mandatsende'}{w.objekt ? ` · ${w.objekt}` : ''}</span>
                </span>
                <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 800, color: farbe }}>{deDatum(w.datum)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
